import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/Commons/SuspenseLoader';
import SidebarLayout from 'src/layouts/SidebarLayout';
import { PathURL } from 'src/utils/constants/path';
import RefundRateReport from 'src/pages/Reports/RefundRateReport';
import { Settings } from 'src/pages/System/Settings';
import ListProduct from 'src/pages/Products/ListProduct';
import CurrencyRate from 'src/pages/System/CurrencyRate';
import PageMessageStatistic from 'src/pages/Reports/PageMessageStatistic';

// eslint-disable-next-line react/display-name
const Loader = (Component: any) => (props: any) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Dashboards
const Dashboard = Loader(lazy(() => import('src/pages/Dashboard')));

//Report
const SalesReport = Loader(lazy(() => import('src/pages/Reports/SalesReport')));
const DigitalReport = Loader(lazy(() => import('src/pages/Reports/DigitalMarketing')));
const OmicallReport = Loader(lazy(() => import('src/pages/Reports/OmicallReport')));
const CustomerNewReport = Loader(lazy(() => import('src/pages/Reports/CustomerNew')));
const PageUserMessageStatistic = Loader(lazy(() => import('src/pages/Reports/PageUserMessageStatistic')));
//Orders
const ListOrder = Loader(lazy(() => import('src/pages/Orders/ListOrder')));
const CreateOrderPage = Loader(lazy(() => import('src/pages/Orders/Create')));
// Users
const Profile = Loader(lazy(() => import('src/pages/Users/Profile')));
const ListUser = Loader(lazy(() => import('src/pages/Users/ListUser')));
const EditUser = Loader(lazy(() => import('src/pages/Users/Detail')));
const CreateUser = Loader(lazy(() => import('src/pages/Users/Create')));

// Systems
const Team = Loader(lazy(() => import('src/pages/System/Team')));
const ExpectedRevenue = Loader(lazy(() => import('src/pages/System/ExpectedRevenue')));
const SalesClosing = Loader(lazy(() => import('src/pages/System/SalesClosing')));
const MarketingFee = Loader(lazy(() => import('src/pages/System/MarketingFee')));
const PagePermissions = Loader(lazy(() => import('src/pages/System/PagesPermission')));
const RolesPermissions = Loader(lazy(() => import('src/pages/System/RolesPermission')));
const UploadSocialFile = Loader(lazy(() => import('src/pages/System/UploadSocial')));
const MarketingUsername = Loader(lazy(() => import('src/pages/System/MarketingUsername')));
const BankCard = Loader(lazy(() => import('src/pages/System/BankCard')));

//Digital Management
const SetupDigitalMarketing = Loader(lazy(() => import('src/pages/DigitalManagement/SocialAccountDigitalMKApproval')));
const MarketingFeeACtual = Loader(lazy(() => import('src/pages/DigitalManagement/MarketingFeeActual')));
const ListMarketingFeeActual = Loader(lazy(() => import('src/pages/DigitalManagement/ListMarketingFeeActualTable')));

//Social Management
const SocialManagement = Loader(lazy(() => import('src/pages/SocialManagement')));

// Customers
const ListCustomer = Loader(lazy(() => import('src/pages/Customers')));

// SignIn
const Signin = Loader(lazy(() => import('src/pages/Authentication')));

// Forgot password
const ForgotPassword = Loader(lazy(() => import('src/pages/ForgotPassword')));
const UpdatePassword = Loader(lazy(() => import('src/pages/UpdatePassword')));

//Not Found
const Error404Page = Loader(lazy(() => import('src/pages/ErrorPage')));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <BaseLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />
      },
      {
        path: 'signin',
        caseSensitive: true,
        element: <Signin />
      },
      {
        path: 'forgot-password/',
        caseSensitive: true,
        element: <ForgotPassword />
      },
      {
        path: 'forgot-password/:token',
        caseSensitive: true,
        element: <UpdatePassword />
      },
      {
        path: 'not-found',
        caseSensitive: true,
        element: <Error404Page />
      },
      {
        path: '*',
        caseSensitive: true,
        element: <Error404Page />
      }
    ]
  },
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: PathURL.dashboard,
        caseSensitive: true,
        element: <Dashboard />
      },
      {
        path: PathURL.order.list,
        caseSensitive: true,
        element: <ListOrder />
      },
      {
        path: PathURL.order.create,
        caseSensitive: true,
        element: <CreateOrderPage />
      },
      {
        path: PathURL.order.edit,
        caseSensitive: true,
        element: <CreateOrderPage />
      },

      {
        path: PathURL.user,
        caseSensitive: true,
        element: <ListUser />
      },

      //#region user
      {
        path: PathURL.user + '/:id',
        caseSensitive: true,
        element: <EditUser />
      },
      {
        path: PathURL.user + '/create',
        caseSensitive: true,
        element: <CreateUser />
      },
      {
        path: PathURL.profile,
        caseSensitive: true,
        element: <Profile />
      },
      //#endregion end user

      //#region customer
      {
        path: PathURL.customer,
        caseSensitive: true,
        element: <ListCustomer />
      },
      //#endregion end customer

      //#region system
      {
        path: PathURL.system.team,
        caseSensitive: true,
        element: <Team />
      },
      {
        path: PathURL.system.expected_revenue,
        caseSensitive: true,
        element: <ExpectedRevenue />
      },
      {
        path: PathURL.system.sales_closing,
        caseSensitive: true,
        element: <SalesClosing />
      },
      {
        path: PathURL.system.marketing_fee,
        caseSensitive: true,
        element: <MarketingFee />
      },
      {
        path: PathURL.system.marketing_username,
        caseSensitive: true,
        element: <MarketingUsername />
      },
      {
        path: PathURL.system.pagesPermission,
        caseSensitive: true,
        element: <PagePermissions />
      },
      {
        path: PathURL.system.rolesPermission,
        caseSensitive: true,
        element: <RolesPermissions />
      },
      {
        path: PathURL.system.uploadSocial,
        caseSensitive: true,
        element: <UploadSocialFile />
      },
      {
        path: PathURL.system.bankCard,
        caseSensitive: true,
        element: <BankCard />
      },
      {
        path: PathURL.system.setting,
        caseSensitive: true,
        element: <Settings />
      },
      {
        path: PathURL.system.currencyRate,
        caseSensitive: true,
        element: <CurrencyRate />
      },
      //#endregion end system

      //#region report
      {
        path: PathURL.report.sales_report,
        caseSensitive: true,
        element: <SalesReport />
      },
      {
        path: PathURL.report.digital_report,
        caseSensitive: true,
        element: <DigitalReport />
      },
      {
        path: PathURL.report.refund_rate_report,
        caseSensitive: true,
        element: <RefundRateReport />
      },
      {
        path: PathURL.report.omicall_report,
        caseSensitive: true,
        element: <OmicallReport />
      },
      {
        path: PathURL.report.new_customer_report,
        caseSensitive: true,
        element: <CustomerNewReport />
      },
      {
        path: PathURL.report.page_message_statistic,
        caseSensitive: true,
        element: <PageMessageStatistic />
      },
      {
        path: PathURL.report.page_user_message_statistic,
        caseSensitive: true,
        element: <PageUserMessageStatistic />
      },
      //#endregion end report

      //#region Digital Management
      {
        path: PathURL.digital_management.setup_digital_marketing,
        caseSensitive: true,
        element: <SetupDigitalMarketing />
      },
      {
        path: PathURL.digital_management.other_cost,
        caseSensitive: true,
        element: <MarketingFeeACtual otherCost={true} />
      },
      {
        path: PathURL.digital_management.other_cost + '/:id',
        caseSensitive: true,
        element: <MarketingFeeACtual otherCost={true} />
      },
      {
        path: PathURL.digital_management.ads_cost + '/:id',
        caseSensitive: true,
        element: <MarketingFeeACtual />
      },
      {
        path: PathURL.digital_management.ads_cost,
        caseSensitive: true,
        element: <MarketingFeeACtual />
      },
      {
        path: PathURL.digital_management.marketing_fee_actual_index,
        caseSensitive: true,
        element: <ListMarketingFeeActual />
      },
      //#endregion

      //#region Social Management
      {
        path: PathURL.social_management.social_index,
        caseSensitive: true,
        element: <SocialManagement />
      }
    ]
  },
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: PathURL.product.list,
        caseSensitive: true,
        element: <ListProduct />
      }
    ]
  }
];

export default routes;
